import React, { useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons'
import { CompetencyArea, CompetencyWithEvaluation } from '../../types/GrowthWheel'
import CompetencyAreaViewCard from '../../components/GrowthWheelCards/competency-area-view-card'
import CompetencyViewCard from '../../components/GrowthWheelCards/competency-view-card'
import { CompetencyStatus, getCompletedCompetencies, getListTransitionClass, getPageTransitionClass, getTotalCompetencies } from '../../utils/growth-wheel-utils'
import GrowthWheelPagination from '../../components/GrowthWheelPagination/GrowthWheelPagination'

interface GrowthWhellCompetencyViewProps {
  currentCompetencyArea: CompetencyArea
  handleClickAreaChange: (competencyArea: CompetencyArea | null) => void
  userCompetencyAreasList: CompetencyArea[]
}

const GrowthWhellCompetencyAreaView = ({ currentCompetencyArea, handleClickAreaChange, userCompetencyAreasList }: GrowthWhellCompetencyViewProps) => {
  const currentAreaIndex = userCompetencyAreasList.findIndex((area) => area.id === currentCompetencyArea.id) ?? 0
  const competencies = currentCompetencyArea.competencies ?? []
  const totalCompetencies = getTotalCompetencies(competencies)
  const completedCompetencies = getCompletedCompetencies(competencies)
  const [transitionDirection, setTransitionDirection] = useState<'left' | 'right' | null>(null)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const previousAreaIndexRef = useRef(currentAreaIndex)

  useEffect(() => {
    const previousIndex = previousAreaIndexRef.current
    if (previousIndex === currentAreaIndex) {
      return
    }

    const direction = currentAreaIndex > previousIndex ? 'left' : 'right'
    setTransitionDirection(direction)
    setIsTransitioning(true)
    previousAreaIndexRef.current = currentAreaIndex

    const timeout = window.setTimeout(() => {
      setIsTransitioning(false)
    }, 280)

    return () => window.clearTimeout(timeout)
  }, [currentAreaIndex])

  const getCompetencyCardProps = (competency: CompetencyWithEvaluation) => {

    const completedCompetency = competency.evaluation?.selfLevel !== null ? CompetencyStatus.COMPLETE : CompetencyStatus.PENDING
    const showSelfAssessed = completedCompetency === CompetencyStatus.COMPLETE
    const showGuided = completedCompetency === CompetencyStatus.COMPLETE
    const selfLevel = competency.evaluation?.selfLevel ?? null
    const guidedLevel = competency.evaluation?.leaderLevel ?? null

    return {
      status: completedCompetency,
      showSelfAssessed: showSelfAssessed,
      showGuided: showGuided,
      selfLevel: selfLevel,
      guidedLevel: guidedLevel,
    }
  }

  const handleClickPreviousArea = (index: number) => {
    const previousArea = userCompetencyAreasList[index]
    if (previousArea) {
      handleClickAreaChange(previousArea)
    }
  }

  const handleClickNextArea = (index: number) => {
    const nextArea = userCompetencyAreasList[index]
    if (nextArea) {
      handleClickAreaChange(nextArea)
    }
  }



  return (
    <div className="container mb-5 gw-competency-area-view">
      <button
        className="gw-competency-area-back"
        type="button"
        onClick={() => handleClickAreaChange(null)}
      >
        <FontAwesomeIcon icon={faChevronLeft} />
        Back to all areas
      </button>

      <div className={`gw-competency-area-view-content gw-competency-area-page ${getPageTransitionClass(isTransitioning, transitionDirection)}`}>
        <CompetencyAreaViewCard
          title={currentCompetencyArea.name}
          description={currentCompetencyArea.description}
          completed={completedCompetencies}
          total={totalCompetencies}
        />

        <div className="gw-competency-area-competencies">
          <h3 className="gw-competency-area-competencies-title">Competencies</h3>
          <div className={`gw-competency-area-competencies-list ${getListTransitionClass(isTransitioning, transitionDirection)}`}>
            {competencies.map((competency) => {
              return (
                <CompetencyViewCard
                  key={competency.id}
                  title={competency.name}
                  description={competency.description}
                  {...getCompetencyCardProps(competency)}
                />
              )
            })}
          </div>
        </div>
      </div>

      <GrowthWheelPagination
        competencies={competencies}
        currentAreaIndex={currentAreaIndex}
        userCompetencyAreasList={userCompetencyAreasList}
        handleClickPreviousArea={handleClickPreviousArea}
        handleClickNextArea={handleClickNextArea}
      />
    </div>
  )
}

export default GrowthWhellCompetencyAreaView